<template>
  <div class="page-core">
    <!-- HEADER -->
    <site-header is-static />

    <v-main>
      <!-- MAIN -->
      <div
        v-if="!isLoading"
        class="app-content site-width"
      >
        <validation-observer
          ref="formValidationObserver"
          v-slot="formObserver"
          tag="div"
          class="form-core-wrapper"
        >
          <!-- BREADCRUMBS -->
          <steps
            v-if="$vuetify.breakpoint.smAndDown"
            :steps="formConfigData"
            @go-to-step="(val) => onGoToStep({...val,formObserver})"
          />

          <!-- CTA -->
          <div class="app-content__spacer">
            <qz-cta
              class="page-core-form__cta"
              :title="activeStepTitle"
              :current-step="currentStep"
            >
              <template #firstStepInfo>
                <p>
                  <strong>Let's save some money!</strong>
                  Take care to answer questions accurately. Incomplete information could see your policy cancelled or a claim rejected.
                </p>
              </template>
            </qz-cta>

            <div
              class="core-form d-block d-md-flex"
              sticky-container
            >
              <aside
                class="
                  core-form__sidebar
                  d-none
                  d-md-block
                "
              >
                <list-steps
                  v-sticky="$vuetify.breakpoint.mdAndUp"
                  :steps="formConfigData"
                  :form-observer="petPatchObserver(formObserver)"
                  :current-step="currentStep"
                  :current-driver="currentDriver"
                  @go-to-group="onGoToGroup"
                  @go-to-step="onGoToStep"
                />
              </aside>
              <div class="core-form__content ml-md-6">
                <form
                  id="form"
                  novalidate
                  class="form-core"
                  @submit.prevent
                  @keydown.enter.prevent
                >
                  <!-- FORM STEPS -->
                  <component
                    :is="stepComponent"
                    :key="componentKey"
                    ref="step"
                    :form="form"
                    :questions="questions"
                    :step-id="activeStep.id"
                    :product-name="productName"
                    :product-title="formMetadata.productTitle"
                    :form-observer="petPatchObserver(formObserver)"
                    :is-continue-triggered="isContinueTriggered"
                    :is-pet-minimise-helptext-experiment="isPetMinimiseHelptextExperiment"
                    class="form-core__step"
                    @valid="onValid"
                    @toast="showToast"
                    @show-modal="showConfirmModal"
                    @no-continue="noContinue = $event"
                    @loading="isProcessing = $event"
                    @step-data="stepData = $event"
                    @question-observer="onQuestionObserver"
                    @edit-process="isEditProcess = $event"
                    @tracking="gtmTracking"
                    @multiform-questions-details="multiformQuestionsDetails = $event"
                    @multiform-changed="isMultiformChanged = $event"
                    @reset-continue-trigger="isContinueTriggered = $event"
                    @show-form-actions="showPetFormActions = $event"
                  />

                  <!-- ERRORS SECTION -->
                  <step-errors
                    v-if="isValidated && formObserver.failed"
                    id="form-errors"
                    :step-ref="$refs.step"
                    :questions="questions"
                    :component-key="componentKey"
                    :multiform-questions-details="multiformQuestionsDetails"
                    :question-observers="questionObservers"
                    :step-id="activeStep.id"
                    class="mt-6"
                    slim
                  />

                  <qz-form-actions
                    ref="formActions"
                    :current-step="currentStep"
                    :form-observer="formObserver"
                    :component-key="componentKey"
                    :show-actions="petShowActions"
                    :is-last-step="isLastStep"
                    @prev-step="prevStep(formObserver)"
                    @next-step="nextStep(formObserver)"
                    @captcha-submit="validateBeforeRecaptcha(formObserver)"
                  />
                </form>
              </div>
            </div>
          </div>
        </validation-observer>
      </div>

      <quote-limit-exceed-popup v-model="quoteLimitExceeded" />

      <!-- FOOTER -->
      <site-footer
        v-if="config.partnerFooter && !isLoading"
        role="footer"
      >
        <div v-html="config.partnerFooterText || ''" />
      </site-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />

      <bespoke-footer-information
        v-if="showBespokeFooterInformation"
        :content="config.bespokeFooterInformation"
      />
    </v-main>

    <!-- LOADING OVERLAY -->
    <page-loader :value="isLoading || isProcessing" />
  </div>
</template>

<script>
import {
  inject,
  computed,
} from '@vue/composition-api';

import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneForm from '@/composables/useQuotezoneForm';
import { getQuotePayload } from '@/payloadTemplates/pet';
import { partnerFooters } from '@/helpers/common';

import Steps from '@/components/Steps/Steps';
import StepErrors from '@/components/StepErrors/StepErrors';

import Step1 from '@/views/pet/steps/Step1.vue';
import Step2 from '@/views/pet/steps/Step2.vue';

import QzCta from '@/components/QzCta/QzCta.vue';
import ListSteps from '@/components/ListSteps/ListSteps.vue';
import QzFormActions from '@/components/QzFormActions.vue';

export default {
  name: 'page-pet-insurance',
  inject: ['formMetadata'],
  metaInfo () {
    const defaultTitle = `${this.config.partnerText} | ${this.formMetadata.productTitle} Insurance`;
    return {
      title: this.config.partnerPageFormTitle || defaultTitle,
    };
  },
  components: {
    QzFormActions,
    ...partnerFooters,
    Step1,
    Step2,
    QzCta,
    ListSteps,
    Steps,
    StepErrors,
  },
  data () {
    return {
      multiformQuestionsDetails: [],
      isMultiformChanged: false,
      questionObservers: {},
      showPetFormActions: false,
    };
  },
  computed: {
    petShowActions () {
      return this.showActions && this.showPetFormActions;
    },
    questions () {
      const questions = this.formConfigData[this.currentStep - 1].questions;

      if (this.isMxDrLivePet && this.currentStep === 2) {
        // Update options for title question
        return questions.map((question) => {
          if (question.questionId === 'title') {
            question.params.optionsConfig.options = this.helpers.getTitleOptions(this.isMxDrLivePet);
          }
          return question;
        });
      }

      const collapsibleExperimentHelpTextQuestions = [
        'pet-type-of-cover',
        'cover-amount',
      ];

      if (!this.isPetMinimiseHelptextExperiment) return questions;

      return questions.map((question) => {
        if (collapsibleExperimentHelpTextQuestions.includes(question.questionId)) question.params.helpText.collapsible = true;
        return question;
      });
    },
  },
  async created () {
    await this.init();
    this.clearLocalStorage();
    // TODO:refactor - move to showActions computed on refactor
    this.showPetFormActions = this.setPetFormActions();
  },
  setup (props, context) {
    const structure = inject('formMetadata');
    const config = inject('config');
    const helpers = inject('helpers');

    const {
      isMxDrLivePet,
      isPetMinimiseHelptext,
    } = inject('abTastyControllerUI');

    // COMPUTED
    const isPetMinimiseHelptextExperiment = computed(() => isPetMinimiseHelptext.value && helpers.isQuotezonePartner(config.partner));

    return {
      ...useQuotezoneCommon(null, context),
      ...useQuotezoneForm({
        payload: getQuotePayload(inject('config')),
        structure: structure.steps,
      }, context),
      isMxDrLivePet,

      // COMPUTED
      isPetMinimiseHelptextExperiment,

      helpers,
    };
  },
  methods: {
    onQuestionObserver ({ questionId, observer }) {
      this.$set(this.questionObservers, questionId, observer);
    },
    petPatchObserver (formObserver) {
      return {
        ...formObserver,
        changed: formObserver.changed || (this.currentStep === 1 && this.isMultiformChanged),
        validated: this.isValidated,
      };
    },
    setPetFormActions () {
      /* multiform controller that affects form actions only on first step
      so on second step show actions should be always visible */
      return this.currentStep === 2;
    },
  },
};
</script>

<style lang="scss" scoped>
.page-core {
  .app-content {
    &__spacer {
      padding-top: _percentage(35, $site-width);
    }
  }
}

.page-core-form {
  &__cta {
    & + * {
      margin-top: _percentage(35, $site-width);
    }
  }
}

.core-form {
  display: flex;
  align-items: flex-start;
  position: relative;

  &__content {
    flex: 1;
  }

  &__sidebar {
    flex: 0 0 190px;
    width: 190px;
  }
}

</style>

<style lang="scss">
.form-core {
  .form-section + .form-section { margin-top: -1px; }

  &__step {
    border-radius: 6px;
    background: $clr-white;
    border: 1px solid $clr-heather;
    padding: 5px 0;
  }
}
</style>

<style lang="scss" scoped src="@/assets/scss/core-products/product-insurance.scss"/>
<style lang="scss" scoped src="@/assets/scss/page-width.scss"/>
